/* eslint-disable react/no-unescaped-entities */
import React from "react";
import { Heart, Globe2, ShieldCheck, Sparkles, Users } from "lucide-react";
import PageHero from "@/components/landing/PageHero";
import PageBottomCTA from "@/components/landing/PageBottomCTA";
import PageMeta from "@/components/PageMeta";

const VALUES = [
  { icon: Heart, title: "Start with the real day", body: "We design around a busy counter: limited time, changing stock, customer relationships, staff handovers, and imperfect connectivity." },
  { icon: Globe2, title: "Familiar by design", body: "Nine languages, regional currencies, and plain workflows help the system meet people where they already work." },
  { icon: ShieldCheck, title: "Ownership before access", body: "Business data belongs to the business. Permissions, branch scope, and activity records protect who can see and change it." },
  { icon: Sparkles, title: "Useful before impressive", body: "A feature earns its place when it removes a step, answers a real question, or prevents an avoidable mistake." },
  { icon: Users, title: "Listen at workflow level", body: "Support is product work. We look for the exact moment a task becomes confusing, slow, or unreliable—and improve that moment." },
];

export default function About() {
  return (
    <>
      <PageMeta title="Why Shopbook Exists" description="Shopbook helps independent retailers replace fragmented records and closing-time uncertainty with an operation that can explain itself." />
      <PageHero
        eyebrow="About Shopbook"
        title="Retail is human."
        italicTail="Its systems should be too."
        intro="Shopbook helps independent retailers and growing multi-location operators replace scattered records with one operating picture they can use every day."
      />

      {/* Mission story */}
      <section className="bg-white py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-12 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <span className="text-xs md:text-sm tracking-[0.2em] uppercase font-bold text-[#D96C4A]">Our story</span>
            <h2 className="mt-4 font-display text-3xl md:text-5xl font-semibold leading-[1.05] text-[#0A2424]">
              It started with one closing-time question.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-5 text-[17px] text-[#3a4a4a] leading-[1.75]">
            <p>
              Why can a business be busy all day and still leave its owner guessing at night? What sold? What changed in stock? Who bought on credit? Which item is about to run out? What did each branch actually do?
            </p>
            <p>
              Those answers often live across notebooks, chat messages, receipts, spreadsheets, and the memory of whoever was at the counter. The problem is not effort. It is that the business has no single, dependable memory.
            </p>
            <p>
              Shopbook was shaped around a direct answer: capture each important action where it happens, preserve its context, and make the full operation understandable from anywhere. Mobile keeps work moving at the counter. Web gives owners and managers the wider view. Plain language keeps both useful to the people doing the work.
            </p>
            <p className="font-display text-[#0A2424] text-2xl leading-snug">
              The ambition is simple: no matter how busy the day becomes, the business should be able to explain itself.
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="bg-[#F7FAF9] py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <div className="max-w-3xl">
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#D96C4A]">What we believe</span>
            <h2 className="mt-4 font-display text-3xl md:text-5xl font-semibold leading-[1.05] text-[#0A2424]">
              Five standards for building something worth trusting.
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {VALUES.map((v) => (
              <article key={v.title} className="rounded-3xl bg-white p-7 border border-[#0E6B6B]/8 shadow-[0_8px_30px_rgb(0,0,0,0.03)]">
                <div className="h-11 w-11 rounded-2xl bg-[#0E6B6B] text-white flex items-center justify-center mb-5">
                  <v.icon className="h-5 w-5" strokeWidth={1.6} />
                </div>
                <h3 className="font-display text-xl font-semibold text-[#0A2424]">{v.title}</h3>
                <p className="mt-2 text-[15px] text-[#4B5E5E] leading-relaxed">{v.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-[#0A3B3B] text-white py-20 md:py-28 relative overflow-hidden">
        <div className="absolute inset-0 bg-kente opacity-[0.10]" />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-12">
          <div className="max-w-2xl">
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#FFD8A8]">By the numbers</span>
            <h2 className="mt-4 font-display text-3xl md:text-5xl font-semibold leading-[1.05]">
              Practical foundations, built into the product.
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { k: "9", v: "Supported languages" },
              { k: "Web + mobile", v: "Connected workspaces" },
              { k: "Offline-ready", v: "Core mobile workflows" },
              { k: "One record", v: "Across sales, stock and credit" },
            ].map((s) => (
              <div key={s.v}>
                <div className="font-display text-5xl md:text-6xl font-semibold text-[#FFD8A8]">{s.k}</div>
                <div className="text-sm text-white/70 mt-2 max-w-[160px]">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <PageBottomCTA
        eyebrow="Build a clearer business"
        title="The business should become easier to understand as it grows."
        body="Whether you operate one counter or several locations, Shopbook keeps the next decision connected to what actually happened."
      />
    </>
  );
}
